import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FaPencilAlt, FaTrash } from "react-icons/fa";
import Table from "../../components/Table";
import AddDialog from "../../components/Modal/AddModal";
import TextInput from "../../components/Select/InputText";
import SelectInput from "../../components/Select/SelectInput";
import ToggleSwitch from "../../components/Select/ToggleSwitch";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../utils/permissions";
import {
    addCourt,
    Court,
    CourtAvailability,
    deleteCourt,
    getCourts,
    updateCourt,
} from "../../services/courtService";
import { showErrorToast, showSuccessToast } from "../../services/toastService";

const dayOptions = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
].map((day) => ({ value: day, label: day }));

const formatAvailability = (availability: CourtAvailability[]) => {
    if (!availability?.length) return "-";
    return availability.map((slot) => `${slot.day} ${slot.startTime}–${slot.endTime}`).join(" • ");
};

const Courts = () => {
    const { user } = useAuth();
    const canEdit = hasPermission(user, "courts.write");
    const [courts, setCourts] = useState<Court[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);

    const loadCourts = async () => {
        try {
            setCourts(await getCourts(true));
        } catch (error) {
            console.error("Failed to fetch courts", error);
            showErrorToast("Failed to load courts");
        }
    };

    useEffect(() => {
        if (user?.id) loadCourts();
    }, [user?.id]);

    const formik = useFormik({
        initialValues: {
            name: "",
            status: true,
            availabilityDay: "",
            availabilityStart: "",
            availabilityEnd: "",
            availability: [] as CourtAvailability[],
        },
        validationSchema: Yup.object({
            name: Yup.string().trim().required("Court name is required").max(100),
            availability: Yup.array().min(1, "Add at least one availability time"),
        }),
        onSubmit: async (values) => {
            try {
                const payload = {
                    name: values.name.trim(),
                    status: values.status,
                    availability: values.availability,
                };
                if (editingId) {
                    const updated = await updateCourt(editingId, payload);
                    setCourts((current) => current.map((court) => court._id === updated._id ? updated : court));
                    showSuccessToast("Court updated");
                } else {
                    const created = await addCourt(payload);
                    setCourts((current) => [created, ...current]);
                    showSuccessToast("Court added");
                }
                closeDialog();
            } catch (error: any) {
                showErrorToast(error?.response?.data?.error || "Failed to save court");
            }
        },
    });

    const openAddDialog = () => {
        setEditingId(null);
        formik.resetForm();
        setDialogOpen(true);
    };

    const openEditDialog = (court: Court) => {
        setEditingId(court._id);
        formik.setValues({
            name: court.name,
            status: court.status,
            availabilityDay: "",
            availabilityStart: "",
            availabilityEnd: "",
            availability: court.availability || [],
        });
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingId(null);
        formik.resetForm();
    };

    const addAvailability = () => {
        const { availabilityDay: day, availabilityStart: startTime, availabilityEnd: endTime } = formik.values;
        if (!day || !startTime || !endTime) {
            showErrorToast("Choose a day, start time, and end time");
            return;
        }
        if (startTime >= endTime) {
            showErrorToast("End time must be after start time");
            return;
        }
        formik.setFieldValue("availability", [...formik.values.availability, { day, startTime, endTime }]);
        formik.setFieldValue("availabilityDay", "");
        formik.setFieldValue("availabilityStart", "");
        formik.setFieldValue("availabilityEnd", "");
    };

    const handleDelete = async (court: Court) => {
        if (!window.confirm(`Delete ${court.name}?`)) return;
        try {
            await deleteCourt(court._id);
            setCourts((current) => current.filter((item) => item._id !== court._id));
            showSuccessToast("Court deleted");
        } catch (error: any) {
            showErrorToast(error?.response?.data?.error || "Failed to delete court");
        }
    };

    const columns = [
        { Header: "Court Name", accessor: "name" },
        {
            Header: "Availability",
            accessor: "availability",
            Cell: ({ value }: { value: CourtAvailability[] }) => (
                <span className="whitespace-normal">{formatAvailability(value)}</span>
            ),
        },
        {
            Header: "Status",
            accessor: "status",
            Cell: ({ value }: { value: boolean }) => (
                <span className={value ? "text-green-600" : "text-red-600"}>{value ? "Active" : "Inactive"}</span>
            ),
        },
        ...(canEdit ? [{
            Header: "Actions",
            accessor: "actions",
            Cell: ({ row }: any) => (
                <div className="flex items-center gap-3">
                    <button type="button" onClick={() => openEditDialog(row.original)} className="text-blue-600" aria-label="Edit court">
                        <FaPencilAlt />
                    </button>
                    <button type="button" onClick={() => handleDelete(row.original)} className="text-red-600" aria-label="Delete court">
                        <FaTrash />
                    </button>
                </div>
            ),
        }] : []),
    ];

    return (
        <div className="w-full p-3 md:p-6">
            <div className="mb-4">
                <h1 className="text-2xl font-bold">Courts</h1>
                <p className="mt-1 text-sm text-gray-500">Add courts and define when each one can be booked.</p>
            </div>
            <Table columns={columns} data={courts} pageSize={8} add={canEdit} onOpen={openAddDialog} />

            <AddDialog title={editingId ? "Edit Court" : "Add Court"} isOpen={dialogOpen} onClose={closeDialog} maxWidth="max-w-2xl">
                <form onSubmit={formik.handleSubmit} className="space-y-5">
                    <TextInput label="Court Name" name="name" value={formik.values.name} onChange={formik.handleChange} placeholder="e.g. Padel Court 1" />
                    {formik.touched.name && formik.errors.name && <p className="text-sm text-red-500">{formik.errors.name}</p>}

                    <div className="space-y-3">
                        <p className="text-sm font-semibold">Availability Times</p>
                        <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-[1fr_1fr_1fr_auto]">
                            <SelectInput label="Day" name="availabilityDay" value={formik.values.availabilityDay} onChange={formik.handleChange} options={dayOptions} />
                            <TextInput label="From" type="time" name="availabilityStart" value={formik.values.availabilityStart} onChange={formik.handleChange} />
                            <TextInput label="To" type="time" name="availabilityEnd" value={formik.values.availabilityEnd} onChange={formik.handleChange} />
                            <button type="button" onClick={addAvailability} className="h-10 rounded bg-gray-800 px-4 text-white hover:bg-black">Add</button>
                        </div>
                        {formik.touched.availability && formik.errors.availability && (
                            <p className="text-sm text-red-500">{formik.errors.availability as string}</p>
                        )}
                        <div className="space-y-2">
                            {formik.values.availability.map((slot, index) => (
                                <div key={`${slot.day}-${slot.startTime}-${slot.endTime}-${index}`} className="flex items-center justify-between rounded border bg-gray-50 px-3 py-2 text-sm">
                                    <span>{slot.day}: {slot.startTime} – {slot.endTime}</span>
                                    <button
                                        type="button"
                                        onClick={() => formik.setFieldValue("availability", formik.values.availability.filter((_, itemIndex) => itemIndex !== index))}
                                        className="font-semibold text-red-600"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <ToggleSwitch label="Active" name="status" checked={formik.values.status} onChange={formik.handleChange} />
                    <button type="submit" className="rounded-lg bg-[#ac120c] px-5 py-2 text-white hover:bg-[#8f0f0a]">
                        {editingId ? "Save Changes" : "Add Court"}
                    </button>
                </form>
            </AddDialog>
        </div>
    );
};

export default Courts;
